export const CONSENT_ACTIONS = ['grant', 'deny', 'revoke'] as const
export type ConsentAction = (typeof CONSENT_ACTIONS)[number]

export const CONSENT_DATA_CATEGORIES = [
  'profile',
  'campaign_operations',
  'content_submission',
  'performance',
  'ai_assistance',
] as const
export type ConsentDataCategory = (typeof CONSENT_DATA_CATEGORIES)[number]

export type ConsentEvent = Readonly<{
  workspaceId: string
  userId: string
  purpose: string
  category: ConsentDataCategory
  policyVersion: number
  action: ConsentAction
  effectiveAt: string
  createdAt: string
}>

export type ConsentState = Readonly<{
  purpose: string
  category: ConsentDataCategory
  policyVersion: number
  status: 'granted' | 'denied' | 'revoked' | 'missing'
  effectiveAt: string | null
}>

function validDate(value: string): number | null {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function validateConsentDecision(input: Pick<ConsentEvent, 'purpose' | 'category' | 'policyVersion' | 'action' | 'effectiveAt'>): void {
  const purpose = input.purpose.trim()
  if (purpose.length < 2 || purpose.length > 120) throw new Error('invalid_consent_purpose')
  if (!CONSENT_DATA_CATEGORIES.includes(input.category)) throw new Error('invalid_consent_category')
  if (!Number.isInteger(input.policyVersion) || input.policyVersion < 1) throw new Error('invalid_consent_policy_version')
  if (!CONSENT_ACTIONS.includes(input.action)) throw new Error('invalid_consent_action')
  if (validDate(input.effectiveAt) === null) throw new Error('invalid_consent_effective_at')
}

export function resolveConsentState(
  events: readonly ConsentEvent[],
  purpose: string,
  category: ConsentDataCategory,
  policyVersion: number,
  now = new Date(),
): ConsentState {
  const normalizedPurpose = purpose.trim()
  const matching = events
    .filter((event) => event.purpose === normalizedPurpose && event.category === category && event.policyVersion === policyVersion)
    .filter((event) => {
      const effective = validDate(event.effectiveAt)
      return effective !== null && effective <= now.getTime()
    })
    .sort((a, b) => {
      const effectiveDiff = (validDate(b.effectiveAt) ?? 0) - (validDate(a.effectiveAt) ?? 0)
      if (effectiveDiff !== 0) return effectiveDiff
      return (validDate(b.createdAt) ?? 0) - (validDate(a.createdAt) ?? 0)
    })

  const latest = matching[0]
  if (!latest) {
    return Object.freeze({ purpose: normalizedPurpose, category, policyVersion, status: 'missing', effectiveAt: null })
  }

  return Object.freeze({
    purpose: normalizedPurpose,
    category,
    policyVersion,
    status: latest.action === 'grant' ? 'granted' : latest.action === 'deny' ? 'denied' : 'revoked',
    effectiveAt: latest.effectiveAt,
  })
}

export type DataUseRequest = Readonly<{
  purpose: string
  category: ConsentDataCategory
  policyVersion: number
}>

export type DataUseEligibility = Readonly<{
  eligible: boolean
  reason: 'consent_granted' | 'consent_missing' | 'consent_denied' | 'consent_revoked'
  state: ConsentState
}>

export function evaluateDataUseEligibility(
  events: readonly ConsentEvent[],
  request: DataUseRequest,
  now = new Date(),
): DataUseEligibility {
  const state = resolveConsentState(events, request.purpose, request.category, request.policyVersion, now)
  if (state.status === 'granted') return Object.freeze({ eligible: true, reason: 'consent_granted', state })
  if (state.status === 'denied') return Object.freeze({ eligible: false, reason: 'consent_denied', state })
  if (state.status === 'revoked') return Object.freeze({ eligible: false, reason: 'consent_revoked', state })
  return Object.freeze({ eligible: false, reason: 'consent_missing', state })
}
